import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";

import WelcomePage from "./pages/WelcomePage";
import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";

import Home from "./pages/Home";

import Contenido from "./pages/Contenido";
import RegistrarNuevoPaciente from "./pages/RegistrarNuevoPaciente";
import MiBaseDeDatos from "./pages/MiBaseDeDatos";
import PacienteDetalle from "./pages/basedatos/PacienteDetalle";
import VisitaDetalle from "./pages/basedatos/VisitaDetalle";
import PautasVisita from "./pages/basedatos/PautasVisita";

import CuestionarioRunner from "./pages/basedatos/CuestionarioRunner";

import ValoracionDisfagia from "./pages/ValoracionDisfagia";
import DiagnosticoDisfagiaOrofaringea from "./pages/valoraciondisfagia/DiagnosticoDisfagiaOrofaringea";
import SospechaDisfagiaOrofaringea from "./pages/valoraciondisfagia/SospechaDisfagiaOrofaringea";

import ValoracionNutricional from "./pages/ValoracionNutricional";

import Recomendaciones from "./pages/Recomendaciones";
import RecomendacionesGenerales from "./pages/recomendacion/RecomendacionesGenerales";
import RecomendacionesDieteticasGenerales from "./pages/recomendacion/RecomendacionesDieteticasGenerales";
import RecomendacionesDieteticasEspesantes from "./pages/recomendacion/RecomendacionesDieteticasEspesantes";
import RecomendacionesDieteticasNectar from "./pages/recomendacion/RecomendacionesDieteticasNectar";
import RecomendacionesDieteticasPudin from "./pages/recomendacion/RecomendacionesDieteticasPudin";

import Recetas from "./pages/Recetas";
import RecetasDisfagia from "./pages/receta/RecetasDisfagia";
import MenuDisfagia from "./pages/receta/MenuDisfagia";
import MenuTexturaNectarMiel from "./pages/receta/MenuTexturaNectarMiel";
import MenuTexturaPudin from "./pages/receta/MenuTexturaPudin";

import Ejercicios from "./pages/Ejercicios";

import PautasEjerciciosLogopediaRehabilitacion from "./pages/PautasEjerciciosLogopediaRehabilitacion";
import TecnicasIncrementoSensorial from "./pages/pautas/TecnicasIncrementoSensorial";
import ManiobrasPosturales from "./pages/pautas/ManiobrasPosturales";
import PraxiasNeuromusculares from "./pages/pautas/PraxiasNeuromusculares";
import ManiobrasDeglutoriasCompensatorias from "./pages/pautas/ManiobrasDeglutoriasCompensatorias";

import DisfagiayFarmacos from "./pages/DisfagiayFarmacos";

import FAQs from "./pages/FAQs";
import Formulario from "./pages/faqs/Formulario";

import Perfil from "./pages/Perfil";
import Register from "./pages/Register";

import Layout from "./components/Layout";
import Video from "./pages/Video";

import { auth } from "./firebase/firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";

import { pauseAllMediaExcept, stopSpeech } from "./components/mediaControl";

// 🔹 Guardián que corta audio/tts al cambiar de ruta
function RouteChangeMediaGuard() {
  const location = useLocation();

  useEffect(() => {
    document.querySelectorAll("audio, video").forEach((el) => {
      try {
        el.pause();
      } catch {}
    });
    stopSpeech();
  }, [location.pathname]);

  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // 🔹 Efecto para Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 🔹 Efecto global para pausar otros audios/tts cuando se reproduce un vídeo
  useEffect(() => {
    const onAnyMediaPlay = (ev) => {
      const el = ev.target;
      if (el && el.tagName === "VIDEO") {
        pauseAllMediaExcept(el);
      }
    };
    document.addEventListener("play", onAnyMediaPlay, true);
    return () => document.removeEventListener("play", onAnyMediaPlay, true);
  }, []);

  if (loading) {
    return <div>Cargando...</div>;
  }

  return (
    <Router>
      {/* Guardián global de medios */}
      <RouteChangeMediaGuard />

      {/* 🔸 Contenedor principal */}
      <div className="min-h-screen flex flex-col">
        <div className="flex-grow">
          <Routes>
            {/* Página de bienvenida si no está logueado */}
            <Route
              path="/"
              element={user ? <Navigate to="/home" /> : <WelcomePage />}
            />

            {/* Registro */}
            <Route
              path="/register"
              element={!user ? <Register /> : <Navigate to="/home" />}
            />

            {/* Login */}
            <Route
              path="/login"
              element={!user ? <Login /> : <Navigate to="/home" />}
            />

            {/* ResetPassword */}
            <Route path="/reset-password" element={<ResetPassword />} />

            {/* Videos públicos por slug */}
            <Route path="/video/:nombre" element={<Video />} />

            {/* Rutas privadas */}
            {user && (
              <>
                <Route
                  path="/home"
                  element={
                    <Layout>
                      <Home />
                    </Layout>
                  }
                />
                <Route
                  path="/contenido"
                  element={
                    <Layout>
                      <Contenido />
                    </Layout>
                  }
                />
                <Route
                  path="/registrar-nuevo-paciente"
                  element={
                    <Layout>
                      <RegistrarNuevoPaciente />
                    </Layout>
                  }
                />
                <Route
                  path="/mi-base-de-datos"
                  element={
                    <Layout>
                      <MiBaseDeDatos />
                    </Layout>
                  }
                />
                <Route
                  path="/pacientes/:id"
                  element={
                    <Layout>
                      <PacienteDetalle />
                    </Layout>
                  }
                />
                <Route
                  path="/pacientes/:id/visitas/:vid"
                  element={
                    <Layout>
                      <VisitaDetalle />
                    </Layout>
                  }
                />

                <Route
                  path="/pacientes/:id/visitas/:vid/pautas"
                  element={
                    <Layout>
                      <PautasVisita />
                    </Layout>
                  }
                />

                <Route
                  path="/pacientes/:id/visitas/:vid/cuestionarios/:instrumentoId"
                  element={
                    <Layout>
                      <CuestionarioRunner />
                    </Layout>
                  }
                />
                <Route
                  path="/valoracion-disfagia"
                  element={
                    <Layout>
                      <ValoracionDisfagia />
                    </Layout>
                  }
                />
                <Route
                  path="/valoracion-disfagia/diagnostico-disfagia-orofaringea"
                  element={
                    <Layout>
                      <DiagnosticoDisfagiaOrofaringea />
                    </Layout>
                  }
                />
                <Route
                  path="/valoracion-disfagia/sospecha-disfagia-orofaringea"
                  element={
                    <Layout>
                      <SospechaDisfagiaOrofaringea />
                    </Layout>
                  }
                />
                <Route
                  path="/valoracion-nutricional"
                  element={
                    <Layout>
                      <ValoracionNutricional />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendaciones"
                  element={
                    <Layout>
                      <Recomendaciones />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendacion/recomendaciones-generales"
                  element={
                    <Layout>
                      <RecomendacionesGenerales />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendacion/recomendaciones-dieteticas-generales"
                  element={
                    <Layout>
                      <RecomendacionesDieteticasGenerales />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendacion/recomendaciones-dieteticas-espesantes"
                  element={
                    <Layout>
                      <RecomendacionesDieteticasEspesantes />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendacion/recomendaciones-dieteticas-nectar"
                  element={
                    <Layout>
                      <RecomendacionesDieteticasNectar />
                    </Layout>
                  }
                />
                <Route
                  path="/recomendacion/recomendaciones-dieteticas-pudin"
                  element={
                    <Layout>
                      <RecomendacionesDieteticasPudin />
                    </Layout>
                  }
                />
                <Route
                  path="/recetas"
                  element={
                    <Layout>
                      <Recetas />
                    </Layout>
                  }
                />
                <Route
                  path="/receta/recetas-disfagia"
                  element={
                    <Layout>
                      <RecetasDisfagia />
                    </Layout>
                  }
                />
                <Route
                  path="/receta/menu-disfagia"
                  element={
                    <Layout>
                      <MenuDisfagia />
                    </Layout>
                  }
                />
                <Route
                  path="/receta/menu-textura-nectar-miel"
                  element={
                    <Layout>
                      <MenuTexturaNectarMiel />
                    </Layout>
                  }
                />
                <Route
                  path="/receta/menu-textura-pudin"
                  element={
                    <Layout>
                      <MenuTexturaPudin />
                    </Layout>
                  }
                />
                <Route
                  path="/ejercicios"
                  element={
                    <Layout>
                      <Ejercicios />
                    </Layout>
                  }
                />
                <Route
                  path="/pautas-ejercicios-logopedia-rehabilitacion"
                  element={
                    <Layout>
                      <PautasEjerciciosLogopediaRehabilitacion />
                    </Layout>
                  }
                />
                <Route
                  path="/pautas/tecnicas-incremento-sensorial"
                  element={
                    <Layout>
                      <TecnicasIncrementoSensorial />
                    </Layout>
                  }
                />
                <Route
                  path="/pautas/maniobras-posturales"
                  element={
                    <Layout>
                      <ManiobrasPosturales />
                    </Layout>
                  }
                />
                <Route
                  path="/pautas/praxias-neuromusculares"
                  element={
                    <Layout>
                      <PraxiasNeuromusculares />
                    </Layout>
                  }
                />
                <Route
                  path="/pautas/maniobras-deglutorias-compensatorias"
                  element={
                    <Layout>
                      <ManiobrasDeglutoriasCompensatorias />
                    </Layout>
                  }
                />

                <Route
                  path="/disfagia-y-farmacos"
                  element={
                    <Layout>
                      <DisfagiayFarmacos />
                    </Layout>
                  }
                />
                <Route
                  path="/faqs"
                  element={
                    <Layout>
                      <FAQs />
                    </Layout>
                  }
                />
                <Route
                  path="/faqs/formulario"
                  element={
                    <Layout>
                      <Formulario />
                    </Layout>
                  }
                />
                <Route
                  path="/perfil"
                  element={
                    <Layout>
                      <Perfil />
                    </Layout>
                  }
                />
              </>
            )}

            {/* Redirecciones si no está autenticado */}
            {!user && (
              <>
                <Route path="*" element={<Navigate to="/" />} />
              </>
            )}
          </Routes>
        </div>

      </div>
    </Router>
  );
}

export default App;